/**
 * ItemEvents - Item Use Socket Event Handlers
 *
 * A timed item use — drinking a shield potion, applying a health kit — is
 * server-authoritative: the client asks with `useItem` and learns from these
 * events whether the use started, finished or was interrupted.
 *
 * The state lives on `gameState.itemUses` as `Map<playerId, { ref, startedAt,
 * completesAt }>`, which is what the use bar renders from. It is kind-neutral
 * on purpose: anything the server decides takes time to use can drive it.
 */

import { consumableRegistry } from '../../../shared/ConsumableRegistry.js';
import { resolveSound } from '../../../shared/resolveSound.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const itemEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) return window.console;
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const itemEventsLogger = ClientLogger.getLogger('general') || itemEventsLoggerFallback;

const ItemEvents = {
  /**
   * @param {Socket} socket
   * @param {GameState} gameState
   * @param {Object} callbacks
   * @param {Function} [callbacks.playItemSound] - play an item's `use` sound
   */
  register(socket, gameState, callbacks = {}) {
    const { playItemSound } = callbacks;

    if (!gameState.itemUses) gameState.itemUses = new Map();

    socket.on('itemUseStarted', (data) => {
      if (!data?.playerId || !data.ref) return;

      const now = Date.now();
      gameState.itemUses.set(data.playerId, {
        ref: data.ref,
        startedAt: now,
        completesAt: now + (data.useTime || 0)
      });

      const id = data.ref.split(':')[1];
      const resolved = resolveSound(consumableRegistry.get(id)?.sounds?.use);
      if (resolved && playItemSound) playItemSound(resolved.path, data.playerId, resolved.volume);
    });

    socket.on('itemUseCompleted', (data) => {
      if (!data?.playerId) return;
      gameState.itemUses.delete(data.playerId);
      itemEventsLogger.debug('[ItemEvents] Use completed', data.ref, data.effect);
    });

    socket.on('itemUseCancelled', (data) => {
      if (!data?.playerId) return;
      gameState.itemUses.delete(data.playerId);
      itemEventsLogger.debug('[ItemEvents] Use cancelled', data.ref, data.reason);
    });
  }
};

export { ItemEvents };
